/*
🌙 Premium Theme System for GetMyHive Safety Intelligence
Türkiye'nin En Gelişmiş Acil Durum Sistemi için Premium UI/UX
*/

import { useState, useEffect, createContext, useContext } from 'react';
import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  themeMode: ThemeMode;
  isDarkMode: boolean;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  antdTheme: ThemeConfig;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'getmyhive_theme_preference';

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const useThemeState = () => {
  const getInitialTheme = (): ThemeMode => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
      if (stored && ['light', 'dark', 'auto'].includes(stored)) {
        return stored;
      }
    } catch (error) {
      console.warn('Theme localStorage error:', error);
    }
    return 'auto';
  };

  const [themeMode, setThemeModeState] = useState<ThemeMode>(getInitialTheme);
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  // System preference detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    setSystemPrefersDark(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const isDarkMode = themeMode === 'dark' || (themeMode === 'auto' && systemPrefersDark);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (error) {
      console.warn('Theme save error:', error);
    }
  };

  const toggleTheme = () => {
    const newMode: ThemeMode = isDarkMode ? 'light' : 'dark';
    setThemeMode(newMode);
  };

  // 🎨 PREMIUM THEME CONFIGURATION
  const antdTheme: ThemeConfig = {
    algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    
    token: {
      // 🌟 Brand Colors - GetMyHive Safety Intelligence
      colorPrimary: '#0052CC',           // Deep professional blue
      colorSuccess: '#00A86B',           // Emerald green for safety
      colorWarning: '#FF8B00',           // Amber for warnings  
      colorError: '#E53E3E',             // Red for emergencies
      colorInfo: '#0052CC',              // Consistent with primary
      
      // 🔤 Typography - Maximum Readability
      fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontWeightStrong: 600,
      
      // 📐 Modern Spacing & Borders
      borderRadius: 6,
      borderRadiusLG: 8,
      borderRadiusSM: 4,
      borderRadiusXS: 2,
      
      // 🎯 Professional Spacing
      padding: 16,
      paddingLG: 24,
      paddingSM: 12,
      paddingXS: 8,
      margin: 16,
      marginLG: 24,
      marginSM: 12,
      marginXS: 8,
      
      // ✨ Smooth Animations
      motionDurationFast: '0.1s',
      motionDurationMid: '0.2s',
      motionDurationSlow: '0.3s',
      
      // 🌑 Dark Mode Specific Tokens
      ...(isDarkMode && {
        colorBgContainer: '#1a1d23',      // Soft dark background
        colorBgElevated: '#22252b',       // Elevated surfaces
        colorBgLayout: '#13171c',         // Main layout background
        colorBorder: '#2d3139',           // Subtle borders
        colorBorderSecondary: '#252932',  // Secondary borders
        colorText: '#f7f7f7',             // High contrast text
        colorTextSecondary: '#c9c9c9',    // Secondary text
        colorTextTertiary: '#9ca3af',     // Tertiary text
        colorTextDisabled: '#6b7280',     // Disabled text
        colorFill: 'rgba(255, 255, 255, 0.12)',
        colorFillSecondary: 'rgba(255, 255, 255, 0.08)',
        colorFillTertiary: 'rgba(255, 255, 255, 0.04)',
      }),
    },
    
    components: {
      // 🏗️ Layout Components
      Layout: {
        bodyBg: isDarkMode ? '#13171c' : '#f8fafc',
        headerBg: isDarkMode ? '#1a1d23' : '#ffffff',
        siderBg: isDarkMode ? '#1a1d23' : '#ffffff',
        footerBg: isDarkMode ? '#1a1d23' : '#ffffff',
      },
      
      // 📋 Menu Components
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: isDarkMode ? 'rgba(0, 82, 204, 0.15)' : 'rgba(0, 82, 204, 0.08)',
        itemHoverBg: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
        itemActiveBg: isDarkMode ? 'rgba(0, 82, 204, 0.2)' : 'rgba(0, 82, 204, 0.1)',
        itemColor: isDarkMode ? '#f7f7f7' : '#374151',
        itemSelectedColor: '#0052CC',
        groupTitleColor: isDarkMode ? '#c9c9c9' : '#6b7280',
      },
      
      // 📄 Card Components
      Card: {
        headerBg: isDarkMode ? '#22252b' : '#f8fafc',
        actionsBg: isDarkMode ? '#1a1d23' : '#f8fafc',
        boxShadowTertiary: isDarkMode 
          ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
          : '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
      
      // 📊 Table Components
      Table: {
        headerBg: isDarkMode ? '#22252b' : '#f8fafc',
        rowHoverBg: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        borderColor: isDarkMode ? '#2d3139' : '#e5e7eb',
      },
      
      // 🔘 Button Components
      Button: {
        primaryShadow: isDarkMode 
          ? '0 2px 4px rgba(0, 82, 204, 0.2)' 
          : '0 2px 4px rgba(0, 82, 204, 0.15)',
        defaultBorderColor: isDarkMode ? '#2d3139' : '#d1d5db',
        defaultColor: isDarkMode ? '#f7f7f7' : '#374151',
      },
      
      // 📝 Input Components
      Input: {
        activeBorderColor: '#0052CC',
        hoverBorderColor: '#1976d2',
        colorBgContainer: isDarkMode ? '#22252b' : '#ffffff',
        colorText: isDarkMode ? '#f7f7f7' : '#374151',
      },
      
      // 🔔 Notification Components
      Notification: {
        zIndexPopup: 9999,
        colorBgElevated: isDarkMode ? '#22252b' : '#ffffff',
      },
      
      // 🪟 Modal Components
      Modal: {
        contentBg: isDarkMode ? '#1a1d23' : '#ffffff',
        headerBg: isDarkMode ? '#22252b' : '#f8fafc',
      },
      
      // 📱 Dropdown Components
      Dropdown: {
        zIndexPopup: 9999,
        colorBgElevated: isDarkMode ? '#22252b' : '#ffffff',
      },
      
      // 📈 Statistic Components
      Statistic: {
        titleFontSize: 14,
        contentFontSize: 24,
        fontFamily: 'inherit',
      },
      
      // 🏷️ Tag Components
      Tag: {
        defaultBg: isDarkMode ? '#2d3139' : '#f3f4f6',
        defaultColor: isDarkMode ? '#f7f7f7' : '#374151',
      },
    },
  };

  return {
    themeMode,
    isDarkMode,
    toggleTheme,
    setThemeMode,
    antdTheme,
  };
};

// Theme mode labels
export const getThemeModeLabel = (mode: ThemeMode): string => {
  switch (mode) {
    case 'light':
      return '☀️ Açık Tema';
    case 'dark':
      return '🌙 Koyu Tema';
    case 'auto':
      return '🌓 Otomatik';
    default:
      return '🌓 Otomatik';
  }
};

export const getThemeModeIcon = (mode: ThemeMode): string => {
  switch (mode) {
    case 'light':
      return '☀️';
    case 'dark':
      return '🌙';
    case 'auto':
      return '🌓';
    default:
      return '🌓';
  }
};
